import React, { useState, useEffect } from 'react';
import { 
  Phone, 
  Plus, 
  Search, 
  MapPin, 
  Clock,
  AlertTriangle,
  Building,
  User,
  Edit,
  Trash2,
  Star,
  ExternalLink
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

const getLocalDateString = () => {
  const now = new Date();
  return now.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getLocalTimeZone = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = -new Date().getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
  const mins = String(Math.abs(offset) % 60).padStart(2, '0');
  return `${tz} (GMT${sign}${hours}:${mins})`;
};

const EmergencyContacts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  const emergencyContacts = [
    {
      id: 'EC001',
      name: 'City General Hospital',
      category: 'hospital',
      phone: '+91 80 2222 3333',
      emergency: '+91 80 2222 3334',
      email: 'emergency@citygeneral.com',
      address: '123 Hospital Road, Downtown',
      services: ['Emergency Care', 'Trauma Center', 'ICU'],
      availability: '24/7',
      distance: '2.5 km',
      rating: 4.8,
      isFavorite: true,
      notes: 'Primary emergency facility with full trauma capabilities'
    },
    {
      id: 'EC002',
      name: 'Downtown Medical Center',
      category: 'hospital',
      phone: '+91 80 4444 5555',
      emergency: '+91 80 4444 5556',
      email: 'emergency@downtownmedical.com',
      address: '456 Medical Avenue, Downtown',
      services: ['Emergency Care', 'Cardiology', 'Neurology'],
      availability: '24/7',
      distance: '1.8 km',
      rating: 4.6,
      isFavorite: false,
      notes: 'Specialized in cardiac and neurological emergencies'
    },
    {
      id: 'EC003',
      name: 'Police Control Room',
      category: 'police',
      phone: '+91 100',
      emergency: '+91 100',
      email: 'control@police.gov.in',
      address: 'Police Headquarters, City Center',
      services: ['Law Enforcement', 'Traffic Control', 'Emergency Response'],
      availability: '24/7',
      distance: '3.2 km',
      rating: 4.2,
      isFavorite: true,
      notes: 'Central police control room for all emergency situations'
    },
    {
      id: 'EC004',
      name: 'Fire Department',
      category: 'fire',
      phone: '+91 101',
      emergency: '+91 101',
      email: 'fire@emergency.gov.in',
      address: 'Fire Station, Industrial Area',
      services: ['Fire Fighting', 'Rescue Operations', 'Hazmat Response'],
      availability: '24/7',
      distance: '4.1 km',
      rating: 4.7,
      isFavorite: false,
      notes: 'Primary fire and rescue services'
    },
    {
      id: 'EC005',
      name: 'Ambulance Control',
      category: 'ambulance',
      phone: '+91 102',
      emergency: '+91 102',
      email: 'control@ambulance.gov.in',
      address: 'Emergency Services HQ',
      services: ['Ambulance Dispatch', 'Medical Transport', 'Emergency Coordination'],
      availability: '24/7',
      distance: '0.5 km',
      rating: 4.9,
      isFavorite: true,
      notes: 'Central ambulance control and coordination center'
    },
    {
      id: 'EC006',
      name: 'Suburban Clinic',
      category: 'clinic',
      phone: '+91 80 6666 7777',
      emergency: '+91 80 6666 7778',
      email: 'info@suburbanclinic.com',
      address: '789 Suburban Road, Residential Area',
      services: ['Primary Care', 'Emergency First Aid', 'Minor Procedures'],
      availability: '8 AM - 10 PM',
      distance: '5.2 km',
      rating: 4.4,
      isFavorite: false,
      notes: 'Community clinic for minor emergencies and primary care'
    }
  ];

  const filteredContacts = emergencyContacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.phone.includes(searchTerm) ||
                         contact.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || contact.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryColor = (category) => {
    switch (category) {
      case 'hospital': return 'bg-red-100 text-red-800';
      case 'police': return 'bg-blue-100 text-blue-800';
      case 'fire': return 'bg-orange-100 text-orange-800';
      case 'ambulance': return 'bg-green-100 text-green-800';
      case 'clinic': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'hospital': return Building;
      case 'police': return User;
      case 'fire': return AlertTriangle;
      case 'ambulance': return Phone;
      case 'clinic': return Building;
      default: return Phone;
    }
  };

  const handleAddContact = () => {
    toast.success('Emergency contact added successfully!');
    setShowAddModal(false);
  };

  const handleViewDetails = (contact) => {
    setSelectedContact(contact);
  };

  const handleCall = (phone) => {
    toast.success(`Calling ${phone}...`);
    // In a real app, this would initiate a phone call
  };

  const handleToggleFavorite = (contactId) => {
    toast.success('Contact updated!');
    // In a real app, this would update the favorite status
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="p-8 bg-neutral-50 min-h-screen">
      <div className="mb-10">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2 tracking-tight">Emergency Contacts</h1>
        <p className="text-neutral-500">
          Quick access to emergency services, hospitals, and important contacts
        </p>
        <div className="flex items-center space-x-4 mt-2 text-sm text-neutral-400">
          <Clock className="w-4 h-4" />
          <span>{currentTime.toLocaleTimeString()}</span>
          <span className="text-neutral-400">|</span>
          <span>{getLocalDateString()}</span>
          <span className="text-neutral-400">|</span>
          <span>{getLocalTimeZone()}</span>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <button 
          onClick={() => handleCall('+91 100')}
          className="p-4 bg-primary-50 rounded-xl hover:bg-primary-100 transition-all duration-200 text-center shadow-soft"
        >
          <AlertTriangle className="w-6 h-6 text-primary-500 mx-auto mb-2" />
          <span className="text-sm font-semibold text-primary-900">Police (100)</span>
        </button>
        <button 
          onClick={() => handleCall('+91 101')}
          className="p-4 bg-accent-50 rounded-xl hover:bg-accent-100 transition-all duration-200 text-center shadow-soft"
        >
          <AlertTriangle className="w-6 h-6 text-accent-500 mx-auto mb-2" />
          <span className="text-sm font-semibold text-accent-900">Fire (101)</span>
        </button>
        <button 
          onClick={() => handleCall('+91 102')}
          className="p-4 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-all duration-200 text-center shadow-soft"
        >
          <Phone className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
          <span className="text-sm font-semibold text-emerald-900">Ambulance (102)</span>
        </button>
        <button 
          onClick={() => handleCall('+91 108')}
          className="p-4 bg-neutral-100 rounded-xl hover:bg-neutral-200 transition-all duration-200 text-center shadow-soft"
        >
          <Phone className="w-6 h-6 text-neutral-500 mx-auto mb-2" />
          <span className="text-sm font-semibold text-neutral-900">Emergency (108)</span>
        </button>
      </div>
      {/* Controls */}
      <div className="bg-white rounded-2xl shadow-card border border-neutral-100 p-6 mb-8">
        <div className="flex flex-col lg:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Search Contacts
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-300 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, phone, or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="min-w-[200px]">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Filter by Category
            </label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="input-field"
            >
              <option value="all">All Categories</option>
              <option value="hospital">Hospitals</option>
              <option value="police">Police</option>
              <option value="fire">Fire Department</option>
              <option value="ambulance">Ambulance</option>
              <option value="clinic">Clinics</option>
            </select>
          </div>
          <div>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>Add Contact</span>
            </button>
          </div>
        </div>
      </div>
      {/* Contacts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {filteredContacts.map((contact, index) => {
          const CategoryIcon = getCategoryIcon(contact.category);
          return (
            <motion.div
              key={contact.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card hover:shadow-lg transition-shadow duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center">
                    <CategoryIcon className="w-5 h-5 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900">{contact.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(contact.category)}`}>
                      {contact.category.toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleToggleFavorite(contact.id)}
                    className={`p-1 rounded ${contact.isFavorite ? 'text-yellow-500' : 'text-neutral-300'}`}
                  >
                    <Star className={`w-4 h-4 ${contact.isFavorite ? 'fill-current' : ''}`} />
                  </button>
                  <span className="text-sm text-neutral-400">{contact.distance}</span>
                </div>
              </div>
              <div className="space-y-3 mb-4">
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-neutral-300" />
                  <span className="text-sm text-neutral-900">{contact.phone}</span>
                  <button
                    onClick={() => handleCall(contact.phone)}
                    className="text-primary-600 hover:text-primary-800"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-neutral-300" />
                  <span className="text-sm text-neutral-900">{contact.address}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-neutral-300" />
                  <span className="text-sm text-neutral-900">{contact.availability}</span>
                </div>
              </div>
              <div className="mb-4">
                <p className="text-sm font-medium text-neutral-500 mb-2">Services</p>
                <div className="flex flex-wrap gap-1">
                  {contact.services.map((service, idx) => (
                    <span key={idx} className="text-xs bg-neutral-50 text-neutral-700 px-2 py-1 rounded-full">
                      {service}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-neutral-500">Rating:</span>
                  <span className="text-sm font-semibold text-neutral-900">{contact.rating}/5</span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleViewDetails(contact)}
                    className="text-sm text-primary-600 hover:text-primary-800 font-semibold"
                  >
                    View Details
                  </button>
                  <button className="p-2 text-neutral-300 hover:text-primary-500 transition-colors">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-neutral-300 hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
      {/* Contact Details Modal */}
      {selectedContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4 p-6 border-b border-neutral-100">
              <h2 className="text-xl font-bold text-neutral-900">Contact Details</h2>
              <button
                onClick={() => setSelectedContact(null)}
                className="text-neutral-300 hover:text-neutral-600"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-2 gap-6 p-6">
              <div>
                <h3 className="font-semibold text-neutral-900 mb-3">Contact Information</h3>
                <div className="space-y-2">
                  <p><span className="font-medium">Name:</span> {selectedContact.name}</p>
                  <p><span className="font-medium">Category:</span> {selectedContact.category}</p>
                  <p><span className="font-medium">Phone:</span> {selectedContact.phone}</p>
                  <p><span className="font-medium">Emergency:</span> {selectedContact.emergency}</p>
                  <p><span className="font-medium">Email:</span> {selectedContact.email}</p>
                  <p><span className="font-medium">Address:</span> {selectedContact.address}</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-3">Service Information</h3>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-neutral-700">Availability:</p>
                    <p className="text-sm text-neutral-900">{selectedContact.availability}</p>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-700">Distance:</p>
                    <p className="text-sm text-neutral-900">{selectedContact.distance}</p>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-700">Rating:</p>
                    <p className="text-sm text-neutral-900">{selectedContact.rating}/5</p>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-700">Notes:</p>
                    <p className="text-sm text-neutral-900">{selectedContact.notes}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-neutral-100 p-6">
              <button
                onClick={() => setSelectedContact(null)}
                className="btn-primary"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
      {/* Add Contact Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-md"
          >
            <h2 className="text-xl font-bold text-neutral-900 mb-4 p-6 border-b border-neutral-100">Add Emergency Contact</h2>
            <div className="space-y-4 p-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Contact Name
                </label>
                <input type="text" className="input-field" placeholder="Hospital Name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Category
                </label>
                <select className="input-field">
                  <option>hospital</option>
                  <option>police</option>
                  <option>fire</option>
                  <option>ambulance</option>
                  <option>clinic</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Phone Number
                </label>
                <input type="tel" className="input-field" placeholder="+91 80 1234 5678" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Emergency Number
                </label>
                <input type="tel" className="input-field" placeholder="+91 80 1234 5679" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Address
                </label>
                <input type="text" className="input-field" placeholder="123 Street, City" />
              </div>
            </div>
            <div className="flex space-x-3 mt-6 p-6 border-t border-neutral-100">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-3 border border-neutral-200 rounded-xl text-neutral-700 hover:bg-neutral-50 transition-colors duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAddContact}
                className="flex-1 btn-primary"
              >
                Add Contact
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default EmergencyContacts; 